import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import {
  Headphones,
  Play,
  Pause,
  Mic,
  Search,
  RotateCcw,
  Clock,
  Share2,
  Check,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';

export default function JubileeAudioGallery({ onPlayTrack, currentPlayingTrack, isPlaying, initialFilter = {} }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(initialFilter.year || 'all');
  const [selectedEvent, setSelectedEvent] = useState(initialFilter.eventId || 'all');
  const [selectedCategory, setSelectedCategory] = useState(initialFilter.categoryId || 'all');
  const [copiedId, setCopiedId] = useState(null);

  const allAudios = useMemo(() => jubileeStorageService.getMediaByType('audio'), []);
  const events = useMemo(() => jubileeStorageService.getEvents(), []);
  const categories = useMemo(() => jubileeStorageService.getCategories(), []);

  const filteredAudios = useMemo(() => {
    return jubileeStorageService.searchMedia({
      mediaType: 'audio',
      query: searchQuery,
      year: selectedYear,
      eventId: selectedEvent,
      categoryId: selectedCategory,
    });
  }, [searchQuery, selectedYear, selectedEvent, selectedCategory]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedYear('all');
    setSelectedEvent('all');
    setSelectedCategory('all');
  };

  const handleShare = (audio) => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedId(audio.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const hasActiveFilters = searchQuery !== '' || selectedYear !== 'all' || selectedEvent !== 'all' || selectedCategory !== 'all';
  const availableYears = Array.from(new Set(allAudios.map((a) => a.year))).sort((a, b) => b - a);

  return (
    <div className="py-4">
      <Container>
        {/* Gallery Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="p-2 rounded-3 bg-indigo text-white d-inline-flex" style={{ background: '#4f46e5' }}>
                <Headphones size={22} />
              </div>
              <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                Audio Gallery & Oral History
              </h2>
            </div>
            <p className="text-muted small m-0">
              Listen to historical speeches, oral history recordings, jubilee anthems, and province homilies.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              className="badge px-3 py-2 rounded-pill fw-semibold"
              style={{ background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' }}
            >
              {filteredAudios.length} {filteredAudios.length === 1 ? 'Recording' : 'Recordings'} Available
            </span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-end-0">
                  <Search size={15} className="text-muted" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search audio by title, speaker, keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
              </div>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="all">All Events</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    Year {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="text-end">
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                >
                  <RotateCcw size={13} /> Clear Filters
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Audio List (Modern Audio Library Experience) */}
        {filteredAudios.length > 0 ? (
          <Row className="g-3">
            {filteredAudios.map((audio) => {
              const isThisPlaying = currentPlayingTrack?.id === audio.id && isPlaying;
              const isThisCurrent = currentPlayingTrack?.id === audio.id;

              return (
                <Col key={audio.id} xs={12}>
                  <div
                    className={`audio-card p-3 ${isThisCurrent ? 'border-primary shadow-md' : ''}`}
                    style={{
                      background: isThisCurrent ? '#f5f7ff' : '#ffffff',
                      borderLeft: isThisCurrent ? '4px solid #4f46e5' : '1px solid #e2e8f0',
                    }}
                  >
                    {/* Cover Art / Play Button */}
                    <div className="position-relative" style={{ flexShrink: 0 }}>
                      <img
                        src={audio.thumbnailUrl || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=300&q=80'}
                        alt={audio.title}
                        className="audio-cover-art shadow-sm"
                      />
                      <button
                        className="btn rounded-circle position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center shadow"
                        style={{
                          width: '44px',
                          height: '44px',
                          background: isThisPlaying ? '#198754' : '#4f46e5',
                          color: '#ffffff',
                          border: '2px solid #ffffff',
                        }}
                        onClick={() => onPlayTrack(audio)}
                        title={isThisPlaying ? 'Pause Audio' : 'Play Audio'}
                      >
                        {isThisPlaying ? <Pause size={20} /> : <Play size={20} style={{ marginLeft: '2px' }} />}
                      </button>
                    </div>

                    {/* Audio Details */}
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <span className="badge bg-indigo bg-opacity-10 text-indigo border" style={{ color: '#4338ca', fontSize: '0.72rem' }}>
                          Year {audio.year}
                        </span>
                        {audio.categoryName && (
                          <span className="badge bg-light text-secondary border text-truncate" style={{ fontSize: '0.72rem', maxWidth: '160px' }}>
                            {audio.categoryName}
                          </span>
                        )}
                        {audio.eventName && (
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.72rem' }}>
                            {audio.eventName}
                          </span>
                        )}
                        {isThisPlaying && (
                          <span className="badge bg-success text-white d-flex align-items-center gap-1 animate-pulse" style={{ fontSize: '0.7rem' }}>
                            <span className="spinner-grow spinner-grow-sm" style={{ width: '8px', height: '8px' }} /> Now Playing
                          </span>
                        )}
                      </div>

                      <h5 className="fw-bold text-slate-900 mb-1 fs-6">{audio.title}</h5>
                      <p className="text-muted small mb-2" style={{ lineHeight: '1.45' }}>
                        {audio.description}
                      </p>

                      {/* Meta Tags Row */}
                      <div className="d-flex flex-wrap align-items-center gap-3 text-secondary" style={{ fontSize: '0.8rem' }}>
                        {audio.speaker && (
                          <div className="d-flex align-items-center gap-1">
                            <Mic size={14} className="text-primary" />
                            <strong>Speaker:</strong> {audio.speaker}
                          </div>
                        )}
                        {audio.duration && (
                          <div className="d-flex align-items-center gap-1">
                            <Clock size={14} className="text-warning" />
                            <span>{audio.duration}</span>
                          </div>
                        )}
                        {audio.source && (
                          <div className="d-none d-md-block text-muted">
                            <span>Archive Shelf: {audio.source}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action buttons (Right) */}
                    <div className="d-flex flex-row flex-md-column gap-2 justify-content-end" style={{ flexShrink: 0 }}>
                      <Button
                        variant={isThisPlaying ? 'success' : 'primary'}
                        size="sm"
                        className="d-flex align-items-center justify-content-center gap-2 px-3 py-2 rounded-pill fw-semibold shadow-sm"
                        style={{ minWidth: '120px', background: isThisPlaying ? '#198754' : '#4f46e5', borderColor: 'transparent' }}
                        onClick={() => onPlayTrack(audio)}
                      >
                        {isThisPlaying ? <Pause size={16} /> : <Play size={16} />}
                        <span>{isThisPlaying ? 'Pause' : 'Listen'}</span>
                      </Button>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center justify-content-center gap-1 rounded-pill"
                        onClick={() => handleShare(audio)}
                      >
                        {copiedId === audio.id ? <Check size={14} className="text-success" /> : <Share2 size={14} />}
                        <span className="text-xs">{copiedId === audio.id ? 'Copied' : 'Share'}</span>
                      </Button>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        ) : (
          <div className="text-center py-5 bg-white rounded-4 border my-4">
            <div className="p-3 rounded-circle d-inline-flex mb-3" style={{ background: '#e0e7ff', color: '#4338ca' }}>
              <Headphones size={36} />
            </div>
            <h5 className="fw-bold text-dark">No audio recordings found</h5>
            <p className="text-muted small mb-3">
              Try modifying your search or clearing active filters.
            </p>
            {hasActiveFilters && (
              <Button variant="outline-primary" size="sm" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </Container>
    </div>
  );
}
